/*
  Emily Harrison London: shared chrome behaviour (chrome builder).
  - <announcement-bar>: slider with autoplay, pause on hover and focus, previous/next buttons.
  - Header: desktop dropdowns (hover, click, keyboard, Escape, outside click), the mobile menu
    (focus moves in, Tab trapped, Escape/Close, focus returns, scroll locked) and the search panel.
  - Footer: newsletter accordions open on a submission result; submit buttons show a busy state.
  - Back to top button, and --header-sticky-offset for sticky elements below the header.
  All handlers are scoped to the chrome elements; nothing is attached to unrelated markup.
*/
(() => {
  if (window.__ehlChromeLoaded) return;
  window.__ehlChromeLoaded = true;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  /* ------------------------------------------------------------------ */
  /* Announcement bar                                                    */
  /* ------------------------------------------------------------------ */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-announcement-slide]')];
      if (this.slides.length <= 1) return;
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.animating = false;
      this.duration = 450;
      this.hovered = false;
      this.focused = false;

      this.onPrev = () => this.show(this.index - 1);
      this.onNext = () => this.show(this.index + 1);
      this.prev = this.querySelector('[data-announcement-prev]');
      this.next = this.querySelector('[data-announcement-next]');
      if (this.prev) this.prev.addEventListener('click', this.onPrev);
      if (this.next) this.next.addEventListener('click', this.onNext);

      this.onEnter = () => {
        this.hovered = true;
        this.stop();
      };
      this.onLeave = () => {
        this.hovered = false;
        this.start();
      };
      this.onFocusIn = () => {
        this.focused = true;
        this.stop();
      };
      this.onFocusOut = (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.focused = false;
        this.start();
      };
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this.prev) this.prev.removeEventListener('click', this.onPrev);
      if (this.next) this.next.removeEventListener('click', this.onNext);
    }

    show(target) {
      if (this.animating) return;
      const total = this.slides.length;
      const nextIndex = (target + total) % total;
      if (nextIndex === this.index) return;
      const current = this.slides[this.index];
      const incoming = this.slides[nextIndex];
      this.animating = true;
      current.classList.add('is-leaving');
      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
      current.setAttribute('inert', '');
      incoming.classList.add('is-active');
      incoming.removeAttribute('aria-hidden');
      incoming.removeAttribute('inert');
      window.setTimeout(
        () => {
          current.classList.remove('is-leaving');
          this.index = nextIndex;
          this.animating = false;
        },
        reducedMotion() ? 0 : this.duration
      );
    }

    start() {
      this.stop();
      if (this.dataset.autoplay !== 'true' || reducedMotion() || this.hovered || this.focused) return;
      const interval = Number(this.dataset.interval) || 4000;
      this.timer = window.setInterval(() => this.show(this.index + 1), interval);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

  /* ------------------------------------------------------------------ */
  /* Header                                                              */
  /* ------------------------------------------------------------------ */
  const closeDisclosures = (except) => {
    document.querySelectorAll('[data-header-disclosure][open]').forEach((disclosure) => {
      if (disclosure !== except) disclosure.removeAttribute('open');
    });
  };

  const closeSearches = (except) => {
    document.querySelectorAll('[data-header-search][open]').forEach((search) => {
      if (search !== except) search.removeAttribute('open');
    });
  };

  const initDisclosures = (scope) => {
    scope.querySelectorAll('[data-header-menu-item]').forEach((item) => {
      if (item.dataset.bound) return;
      item.dataset.bound = 'true';
      const disclosure = item.querySelector('[data-header-disclosure]');
      const summary = disclosure && disclosure.querySelector('summary');
      if (!disclosure || !summary) return;
      let closeTimer = null;
      let openedByHover = false;

      disclosure.addEventListener('toggle', () => {
        if (disclosure.open) {
          closeDisclosures(disclosure);
          closeSearches();
        } else {
          openedByHover = false;
        }
      });

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' || !desktopQuery.matches) return;
        window.clearTimeout(closeTimer);
        if (!disclosure.open) {
          openedByHover = true;
          disclosure.open = true;
        }
      });

      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse' || !desktopQuery.matches) return;
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          if (!item.contains(document.activeElement) || openedByHover) disclosure.open = false;
        }, 150);
      });

      // A click on a panel that the pointer already opened keeps it open (the reference opens on click).
      summary.addEventListener('click', (event) => {
        if (openedByHover && disclosure.open) {
          event.preventDefault();
          openedByHover = false;
        }
      });

      disclosure.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !disclosure.open) return;
        event.preventDefault();
        event.stopPropagation();
        disclosure.open = false;
        summary.focus();
      });

      disclosure.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !disclosure.contains(event.relatedTarget)) disclosure.open = false;
      });
    });
  };

  const initSearch = (scope) => {
    scope.querySelectorAll('[data-header-search]').forEach((search) => {
      if (search.dataset.bound) return;
      search.dataset.bound = 'true';
      const summary = search.querySelector('summary');
      search.addEventListener('toggle', () => {
        if (!search.open) return;
        closeDisclosures();
        closeSearches(search);
        const input = search.querySelector('input[type="search"]');
        window.requestAnimationFrame(() => input && input.focus());
      });
      search.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !search.open) return;
        if (event.defaultPrevented) return;
        event.preventDefault();
        search.open = false;
        if (summary) summary.focus();
      });
    });
  };

  const initDrawer = (scope) => {
    scope.querySelectorAll('[data-header-drawer]').forEach((drawer) => {
      if (drawer.dataset.bound) return;
      drawer.dataset.bound = 'true';
      const toggle = drawer.querySelector('[data-header-drawer-toggle]');
      const panel = drawer.querySelector('[data-header-drawer-panel]');
      const closeButton = drawer.querySelector('[data-header-drawer-close]');
      if (!toggle || !panel) return;
      let lastFocused = null;
      let inerted = [];

      const visibleFocusable = () =>
        [...panel.querySelectorAll(FOCUSABLE)].filter((node) => node.offsetParent !== null || node === document.activeElement);

      const onKeydown = (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          drawer.open = false;
          return;
        }
        if (event.key !== 'Tab') return;
        const nodes = visibleFocusable();
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };

      const setInert = (on) => {
        const host = drawer.closest('.shopify-section');
        if (on) {
          inerted = [...document.body.children].filter(
            (node) => node !== host && node.tagName !== 'SCRIPT' && !node.hasAttribute('inert')
          );
          inerted.forEach((node) => node.setAttribute('inert', ''));
        } else {
          inerted.forEach((node) => node.removeAttribute('inert'));
          inerted = [];
        }
      };

      if (closeButton) {
        closeButton.addEventListener('click', (event) => {
          event.preventDefault();
          drawer.open = false;
        });
      }

      drawer.addEventListener('toggle', (event) => {
        if (event.target !== drawer) return;
        if (drawer.open) {
          lastFocused = document.activeElement;
          document.documentElement.classList.add('header-drawer-open');
          panel.addEventListener('keydown', onKeydown);
          closeSearches();
          setInert(true);
          window.requestAnimationFrame(() => {
            const nodes = visibleFocusable();
            (nodes[0] || panel).focus();
          });
        } else {
          document.documentElement.classList.remove('header-drawer-open');
          panel.removeEventListener('keydown', onKeydown);
          setInert(false);
          const target = lastFocused instanceof HTMLElement && document.contains(lastFocused) ? lastFocused : toggle;
          target.focus();
        }
      });

      // The menu only exists below 990px: close it when the viewport grows.
      desktopQuery.addEventListener('change', (event) => {
        if (event.matches && drawer.open) drawer.open = false;
      });
    });
  };

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-header-disclosure]')) closeDisclosures();
    if (!event.target.closest('[data-header-search]')) closeSearches();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    const openSearch = document.querySelector('[data-header-search][open]');
    closeDisclosures();
    if (openSearch) {
      openSearch.open = false;
      const summary = openSearch.querySelector('summary');
      if (summary) summary.focus();
    }
  });

  /* Height of the sticky header, for sticky elements further down the page. */
  const trackHeader = () => {
    const header = document.querySelector('.section-header--sticky');
    if (!header) return;
    const update = () => {
      const sticky = getComputedStyle(header).position === 'sticky';
      document.documentElement.style.setProperty('--header-sticky-offset', sticky ? `${header.offsetHeight}px` : '0px');
    };
    update();
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(header);
    else window.addEventListener('resize', update);
  };

  /* ------------------------------------------------------------------ */
  /* Footer newsletter                                                   */
  /* ------------------------------------------------------------------ */
  const initNewsletter = (scope) => {
    scope.querySelectorAll('.footer__newsletter-form').forEach((form) => {
      if (form.dataset.bound) return;
      form.dataset.bound = 'true';
      form.addEventListener('submit', (event) => {
        if (form.dataset.submitting === 'true') {
          event.preventDefault();
          return;
        }
        const input = form.querySelector('input[type="email"]');
        if (input && !input.checkValidity()) return;
        form.dataset.submitting = 'true';
        const button = form.querySelector('button[type="submit"]');
        if (button) {
          button.setAttribute('aria-busy', 'true');
          window.setTimeout(() => {
            button.disabled = true;
          }, 0);
        }
      });
    });

    // After Shopify returns the submission result, show it: open the accordion that holds it
    // (only the visible copy of the footer) and move focus to the message.
    const messages = [...scope.querySelectorAll('[data-newsletter-message]')].filter((node) => {
      const accordion = node.closest('[data-footer-accordion]');
      return desktopQuery.matches ? !accordion : Boolean(accordion);
    });
    messages.forEach((message) => {
      const accordion = message.closest('[data-footer-accordion]');
      if (accordion) accordion.open = true;
      window.requestAnimationFrame(() => {
        message.focus({ preventScroll: true });
        message.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      });
    });
  };

  /* ------------------------------------------------------------------ */
  /* Back to top                                                         */
  /* ------------------------------------------------------------------ */
  const initBackToTop = () => {
    const button = document.querySelector('[data-back-to-top]');
    if (!button) return;
    const THRESHOLD = 400;
    let frame = null;
    const update = () => {
      frame = null;
      button.classList.toggle('is-visible', window.scrollY > THRESHOLD);
    };
    window.addEventListener(
      'scroll',
      () => {
        if (frame === null) frame = window.requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
    button.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
      const main = document.getElementById('MainContent');
      if (main) main.focus({ preventScroll: true });
    });
  };

  /* ------------------------------------------------------------------ */
  /* Init and theme editor support                                       */
  /* ------------------------------------------------------------------ */
  const initScope = (scope) => {
    initDisclosures(scope);
    initSearch(scope);
    initDrawer(scope);
    initNewsletter(scope);
  };

  const init = () => {
    initScope(document);
    trackHeader();
    initBackToTop();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', (event) => {
    initScope(event.target);
    trackHeader();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-announcement-slide]');
    const bar = slide && slide.closest('announcement-bar');
    if (bar && bar.slides) {
      bar.stop();
      bar.show(bar.slides.indexOf(slide));
    }
  });
})();
